import { Circle, FabricText, Group, Line, Rect, Textbox } from 'fabric';
import type { FabricObject } from 'fabric';
import { applyFill } from '../fill';
import { registerRecipe } from '../registry';
import { tagSmartObject } from '../types';
import type {
  ContentSourceSpec,
  FillValue,
  ParamField,
  StandardContentPayload,
  ToolRecipe,
} from '../types';

/**
 * Anchor Chart — the general classroom anchor-chart builder: a title banner
 * over N sections, each a heading and bullet lines, in one or two columns. The
 * cross-curricular teaching-display workhorse. Excellent fully manual and
 * Smart-fillable: the graph turns a standard's concepts / skills / vocabulary
 * into Key ideas / We can / Words to know sections.
 *
 * Stock primitives only (Rect / Line / Circle / Textbox / FabricText). Params
 * arrive untrusted; generate() coerces every read, splits bullet text on
 * newlines, and never throws.
 */

function num(v: unknown, fallback: number): number {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : (v as number);
  return typeof n === 'number' && Number.isFinite(n) ? n : fallback;
}
function str(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.trim() !== '' ? v : fallback;
}
function rawStr(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}
function jsonArray<T>(v: unknown, fallback: T[]): T[] {
  let parsed: unknown = v;
  if (typeof v === 'string') {
    try {
      parsed = JSON.parse(v);
    } catch {
      return fallback;
    }
  }
  return Array.isArray(parsed) ? (parsed as T[]) : fallback;
}
function splitLines(v: unknown): string[] {
  return rawStr(v, '')
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}
function solidFromFill(fill: unknown, fallback: string): string {
  if (typeof fill === 'string') return fill.trim() || fallback;
  if (fill && typeof fill === 'object') {
    const f = fill as { type?: string; color?: string; from?: string; stops?: { color?: string }[] };
    if (f.type === 'solid' && typeof f.color === 'string') return f.color;
    if (f.type === 'linear-gradient' || f.type === 'radial-gradient') {
      if (Array.isArray(f.stops) && f.stops.length && f.stops[0].color) return f.stops[0].color;
      if (typeof f.from === 'string') return f.from;
    }
  }
  return fallback;
}
function readableInkOn(bg: string, ink: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(bg).trim());
  if (!m) return ink;
  const n = parseInt(m[1], 16);
  const L = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return L > 0.6 ? ink : '#ffffff';
}
/** First `n` chars, ellipsized on a word-ish boundary. */
function truncate(s: string, n: number): string {
  const t = s.trim();
  if (t.length <= n) return t;
  return `${t.slice(0, n).trimEnd()}…`;
}
function firstSentence(s: string): string {
  const t = s.trim();
  const cut = t.search(/[.!?]/);
  return (cut >= 0 ? t.slice(0, cut) : t).trim();
}

const INK = '#0f172a';
const DEFAULT_FONT = 'Nunito';
const SECTION_CONTENT = 'Content';
const SECTION_LAYOUT = 'Layout';
const SECTION_COLORS = 'Colors';
const SECTION_FONTS = 'Fonts';

const MAX_SECTIONS = 6;

interface SectionItem {
  heading?: unknown;
  bullets?: unknown;
}

const SAMPLE_SECTIONS: SectionItem[] = [
  {
    heading: 'What is a fraction?',
    bullets:
      'A fraction names equal parts of a whole.\nThe denominator tells how many equal parts.\nThe numerator tells how many parts we count.',
  },
  {
    heading: 'Comparing fractions',
    bullets:
      'Same denominator? The bigger numerator wins.\nSame numerator? Fewer, bigger pieces win.\nUse 1/2 as a benchmark to estimate.',
  },
  {
    heading: 'Equivalent fractions',
    bullets: 'Multiply or divide top and bottom by the same number.\n1/2 = 2/4 = 4/8.\nSimplify by dividing out a common factor.',
  },
];

const SECTION_ITEM_SCHEMA: ParamField[] = [
  { key: 'heading', label: 'Section heading', type: 'text' },
  { key: 'bullets', label: 'Bullet points', type: 'multiline', help: 'One per line.' },
];

const contentSource: ContentSourceSpec = {
  kind: 'standard',
  wants: ['concepts', 'skills', 'vocabulary'],
  applyContent(payload: StandardContentPayload, current: Record<string, unknown>) {
    const next = { ...current };
    const sections: { heading: string; bullets: string }[] = [];
    const concepts = Array.isArray(payload?.concepts) ? payload.concepts : [];
    if (concepts.length) {
      sections.push({
        heading: 'Key ideas',
        bullets: concepts
          .slice(0, 6)
          .map((c) => (c?.definition ? `${c.name} — ${truncate(c.definition, 80)}` : c?.name ?? ''))
          .filter(Boolean)
          .join('\n'),
      });
    }
    const skills = Array.isArray(payload?.skills) ? payload.skills : [];
    if (skills.length) {
      sections.push({
        heading: 'We can',
        bullets: skills.slice(0, 6).map((s) => s?.name ?? '').filter(Boolean).join('\n'),
      });
    }
    const vocab = Array.isArray(payload?.vocabulary) ? payload.vocabulary : [];
    if (vocab.length) {
      sections.push({
        heading: 'Words to know',
        bullets: vocab
          .slice(0, 8)
          .map((v) => (v?.definition ? `${v.term} — ${truncate(v.definition, 60)}` : v?.term ?? ''))
          .filter(Boolean)
          .join('\n'),
      });
    }
    // Only overwrite sections when at least one had content — otherwise keep
    // the educator's sections untouched.
    // Gate BOTH sections and title on real content landing (owner 2026-07-24):
    // a title-only patch on an empty payload would misfire the modal's "changed"
    // detection → false "Filled from" banner + suppressed enrichment poll.
    const std = payload?.standard;
    if (sections.length) {
      next.sections = sections;
      if (std) {
        const short = firstSentence(rawStr(std.description, ''));
        next.title = short ? `${std.code} · ${truncate(short, 60)}` : std.code;
      }
    }
    if (std?.code) next.sourceStandardCode = std.code;
    if (std?.id) next.sourceStandardId = std.id;
    return next;
  },
};

const anchorChart: ToolRecipe = {
  id: 'anchor-chart',
  domain: 'ela',
  category: 'decor',
  tags: ['anchor-chart', 'notes', 'reference', 'standards', 'sections', 'display'],
  featured: true,
  name: 'Anchor Chart',
  version: 1,
  description:
    'A build-anything anchor chart — a title banner over sections of headings and bullet points, in one or two columns. Type your own or generate it from a standard.',
  defaults: {
    title: 'Understanding Fractions',
    sections: SAMPLE_SECTIONS.map((s) => ({ ...s })),
    layout: 'single-column',
    bannerFill: '#7c3aed',
    sectionFill: '#f8fafc',
    accentColor: '#7c3aed',
    textColor: INK,
    textFont: DEFAULT_FONT,
    titleFont: '',
    headingFont: '',
    bodyFont: '',
    sourceStandardCode: '',
    sourceStandardId: '',
  },
  schema: [
    { key: 'title', label: 'Title', type: 'text', section: SECTION_CONTENT, help: 'The banner above the chart.' },
    {
      key: 'sections',
      label: 'Sections',
      type: 'list',
      itemSchema: SECTION_ITEM_SCHEMA,
      summaryKeys: ['heading'],
      minItems: 1,
      maxItems: MAX_SECTIONS,
      section: SECTION_CONTENT,
      help: 'One heading + its bullet points per section.',
    },
    {
      key: 'layout',
      label: 'Layout',
      type: 'select',
      section: SECTION_LAYOUT,
      options: [
        { value: 'single-column', label: 'Single column' },
        { value: 'two-column', label: 'Two columns' },
      ],
    },
    { key: 'bannerFill', label: 'Banner fill', type: 'fill', section: SECTION_COLORS, help: 'Solid, gradient, or texture.' },
    { key: 'sectionFill', label: 'Section fill', type: 'fill', section: SECTION_COLORS, help: 'Solid, gradient, or texture.' },
    { key: 'accentColor', label: 'Accent color', type: 'color', section: SECTION_COLORS, help: 'Bullets and heading rules.' },
    { key: 'textColor', label: 'Text color', type: 'color', section: SECTION_COLORS },
    { key: 'textFont', label: 'Text font (default for all parts)', type: 'font', section: SECTION_FONTS },
    { key: 'titleFont', label: 'Title font', type: 'font', section: SECTION_FONTS },
    { key: 'headingFont', label: 'Heading font', type: 'font', section: SECTION_FONTS },
    { key: 'bodyFont', label: 'Body font', type: 'font', section: SECTION_FONTS },
    { key: 'sourceStandardCode', label: 'Source standard code', type: 'text', section: SECTION_CONTENT, advanced: true, help: 'Set automatically when filled from a standard.' },
    { key: 'sourceStandardId', label: 'Source standard id', type: 'text', section: SECTION_CONTENT, advanced: true, help: 'Set automatically when filled from a standard.' },
  ],
  modal: {
    hero: ['title', 'sections'],
    columns: 2,
    sectionOrder: [SECTION_CONTENT, SECTION_LAYOUT, SECTION_COLORS, SECTION_FONTS],
  },
  gradeBands: {
    K2: { textFont: 'Fredoka', layout: 'single-column' },
    G35: { textFont: 'Nunito', layout: 'single-column' },
    G68: { textFont: 'Lexend', layout: 'two-column' },
    G912: { textFont: 'Arial', layout: 'two-column' },
  },
  contentSource,
  async generate(params) {
    const W = 900;
    const pad = 32;
    const bannerH = 108;
    const colGap = 28;
    const secPad = 20;
    const secGap = 22;
    const bulletGap = 10;
    const bulletGutter = 26;
    const headingSize = 26;
    const bulletSize = 20;

    const title = rawStr(params.title, '').trim();
    const layoutRaw = str(params.layout, 'single-column');
    const twoCol = layoutRaw === 'two-column';

    const textColor = str(params.textColor, INK);
    const accentColor = str(params.accentColor, '#7c3aed');
    const textFont = str(params.textFont, DEFAULT_FONT);
    const partFont = (key: string): string => {
      const v = params[key];
      return typeof v === 'string' && v.trim() !== '' ? v : textFont;
    };
    const titleFont = partFont('titleFont');
    const headingFont = partFont('headingFont');
    const bodyFont = partFont('bodyFont');

    const bannerFill = (params.bannerFill ?? '#7c3aed') as FillValue;
    const sectionFill = (params.sectionFill ?? '#f8fafc') as FillValue;

    let sections = jsonArray<SectionItem>(params.sections, SAMPLE_SECTIONS)
      .filter((s): s is SectionItem => Boolean(s) && typeof s === 'object')
      .filter((s) => rawStr(s.heading, '').trim() !== '' || splitLines(s.bullets).length > 0)
      .slice(0, MAX_SECTIONS);
    if (sections.length === 0) sections = SAMPLE_SECTIONS;

    const colCount = twoCol ? 2 : 1;
    const colW = (W - pad * 2 - (colCount - 1) * colGap) / colCount;
    const bulletTextW = Math.max(40, colW - secPad * 2 - bulletGutter);

    // Phase 1 — build & measure each section; compute its height.
    interface BuiltSection {
      heading: string;
      bullets: { box: Textbox; h: number }[];
      height: number;
    }
    const built: BuiltSection[] = sections.map((s) => {
      const heading = rawStr(s.heading, '').trim();
      const bullets = splitLines(s.bullets).map((line) => {
        const box = new Textbox(line, {
          width: bulletTextW,
          fontSize: bulletSize,
          fontFamily: bodyFont,
          fill: textColor,
          textAlign: 'left',
          lineHeight: 1.15,
          originX: 'left',
          originY: 'top',
        });
        return { box, h: box.height ?? bulletSize };
      });
      const headBlock = heading ? headingSize * 1.25 + 6 + 14 : 6;
      const bulletsBlock = bullets.reduce((acc, b) => acc + b.h + bulletGap, 0) - (bullets.length ? bulletGap : 0);
      const height = secPad * 2 + headBlock + Math.max(0, bulletsBlock);
      return { heading, bullets, height };
    });

    // Phase 2 — place sections into the shortest column (balances two columns).
    const bannerBottom = (title ? bannerH : 0) + 24;
    const colHeights = new Array(colCount).fill(bannerBottom);
    const objects: FabricObject[] = [];
    const bannerRects: FabricObject[] = [];
    const sectionRects: FabricObject[] = [];

    for (const s of built) {
      let ci = 0;
      for (let i = 1; i < colCount; i++) if (colHeights[i] < colHeights[ci]) ci = i;
      const x = pad + ci * (colW + colGap);
      const y = colHeights[ci];

      const rect = new Rect({ left: x, top: y, width: colW, height: s.height, rx: 16, ry: 16 });
      sectionRects.push(rect);
      objects.push(rect);

      let cy = y + secPad;
      if (s.heading) {
        objects.push(
          new Textbox(s.heading, {
            left: x + secPad,
            top: cy,
            width: colW - secPad * 2,
            fontSize: headingSize,
            fontFamily: headingFont,
            fontWeight: 'bold',
            fill: textColor,
            textAlign: 'left',
            originX: 'left',
            originY: 'top',
          }),
        );
        const ruleY = cy + headingSize * 1.25 + 4;
        objects.push(new Line([x + secPad, ruleY, x + colW - secPad, ruleY], { stroke: accentColor, strokeWidth: 3, strokeLineCap: 'round' }));
        cy = ruleY + 14;
      } else {
        cy += 6;
      }

      for (const b of s.bullets) {
        objects.push(new Circle({ left: x + secPad + 7, top: cy + bulletSize * 0.6, originX: 'center', originY: 'center', radius: 5, fill: accentColor }));
        b.box.set({ left: x + secPad + bulletGutter, top: cy });
        objects.push(b.box);
        cy += b.h + bulletGap;
      }

      colHeights[ci] = y + s.height + secGap;
    }

    const H = Math.max(...colHeights) - secGap + pad;

    // Transparent bounds so the group is exactly W × H.
    objects.unshift(new Rect({ left: 0, top: 0, width: W, height: H, fill: 'transparent', strokeWidth: 0 }));

    // Banner + title (drawn last so it sits above the section cards' top edge).
    if (title) {
      const banner = new Rect({ left: pad, top: pad, width: W - pad * 2, height: bannerH - pad, rx: 20, ry: 20 });
      bannerRects.push(banner);
      objects.push(banner);
      objects.push(
        new Textbox(title, {
          left: W / 2,
          top: pad + (bannerH - pad) / 2,
          width: W - pad * 2 - 32,
          fontSize: 46,
          fontFamily: titleFont,
          fontWeight: 'bold',
          fill: readableInkOn(solidFromFill(bannerFill, '#7c3aed'), INK),
          textAlign: 'center',
          originX: 'center',
          originY: 'center',
        }),
      );
    }

    await Promise.all([
      ...bannerRects.map((o) => applyFill(o, bannerFill, '#7c3aed')),
      ...sectionRects.map((o) => applyFill(o, sectionFill, '#f8fafc')),
    ]);

    const group = new Group(objects);
    group.set({ left: 0, top: 0 });
    return tagSmartObject(group, anchorChart, params);
  },
};

registerRecipe(anchorChart);
